import React, { useState, MouseEvent, useMemo } from 'react'

// UI
import { secondary } from '../../../components/constants/colors'
import {
  Stack,
  TextField,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'

//icons
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import ListAltIcon from '@mui/icons-material/ListAlt'

import {
  downloadEventsCSV,
  downloadEventsPDF,
  donwloadEventXLSX,
} from '../../../services/export'
import { isNumeric } from '../../../utils/isValid'

interface ExportModalProps {
  siteId: string
  open: boolean
  handleClose: () => void
}

enum Format {
  csv = 'csv',
  pdf = 'pdf',
  xlsx = 'xlsx',
}

enum Length {
  year = 'year',
  all = 'all',
}

function ExportModal({ siteId, open, handleClose }: ExportModalProps) {
  const [formatToggle, setFormatToggle] = useState<Format>(Format.csv)
  const [typeToggle, setTypeToggle] = useState<Length>(Length.all)
  const [yearText, setYearText] = useState(`${new Date().getFullYear()}`)

  const [loading, setLoading] = useState(false)

  const submitButtonIcon = useMemo(() => {
    switch (formatToggle) {
      case Format.csv:
        return <ListAltIcon />
      case Format.xlsx:
        return <ListAltIcon />
      case Format.pdf:
        return <PictureAsPdfIcon />
    }
  }, [formatToggle])

  const handleFormatToggleChange = (
    event: MouseEvent,
    newSelection: Format
  ) => {
    setFormatToggle(newSelection)
  }

  const handleTypeToggleChange = (event: MouseEvent, newType: Length) => {
    setTypeToggle(newType)
  }

  const handleModalClose = () => {
    // Reset state when closed, return to modal with new state
    setFormatToggle(Format.csv)
    setTypeToggle(Length.all)
    setYearText(`${new Date().getFullYear()}`)

    handleClose()
  }

  const handleDonwloadButtonClicked = async () => {
    setLoading(true)
    if (formatToggle === Format.pdf) {
      await downloadEventsPDF(siteId, yearText)
    } else if (formatToggle === Format.csv) {
      await downloadEventsCSV(siteId, yearText)
    } else {
      await donwloadEventXLSX(siteId, yearText)
    }
    setLoading(false)
    handleModalClose() //close dialog
  }

  return (
    <Dialog
      open={open}
      onClose={handleModalClose}
      aria-labelledby="download"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle style={{ textAlign: 'center' }} id="alert-dialog-title">
        {'Export Events'}
      </DialogTitle>
      <DialogContent style={{ justifyContent: 'center', alignItems: 'center' }}>
        <Stack spacing={2}>
          <ToggleButtonGroup
            color="primary"
            fullWidth
            value={formatToggle}
            exclusive
            onChange={handleFormatToggleChange}
            aria-label="Export Format"
          >
            <ToggleButton value={Format.xlsx}>Excel (xlsx)</ToggleButton>
            <ToggleButton value={Format.csv}>Excel (csv)</ToggleButton>
            <ToggleButton value={Format.pdf}>PDF</ToggleButton>
          </ToggleButtonGroup>

          <ToggleButtonGroup
            value={typeToggle}
            exclusive
            onChange={handleTypeToggleChange}
          >
            <ToggleButton value={Length.all}>All</ToggleButton>
            <ToggleButton value={Length.year}>Year</ToggleButton>
          </ToggleButtonGroup>
          {typeToggle === Length.year && (
            <TextField
              value={yearText}
              onChange={(e) => {
                setYearText(e.target.value)
              }}
              inputProps={{ maxLength: 4 }}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {/* csv link to download file */}
        {loading && <CircularProgress />}
        <Button
          variant="contained"
          disabled={typeToggle === Length.year && !isNumeric(yearText)}
          onClick={handleDonwloadButtonClicked}
          style={{ backgroundColor: secondary, color: 'white' }}
          startIcon={submitButtonIcon}
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ExportModal
